import type { Theme } from '@mui/material';
import { Button, Card, CardHeader, Divider, useMediaQuery } from '@mui/material';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { PropertyList } from '../../../../../components/atoms/property-list';
import { PropertyListItem } from '../../../../../components/atoms/property-list-item';
import { UpdateDestinationDialog } from '../../organisms/dialogs/update-destination-dialog/update-destination-dialog';

import { useDialog } from '@/hooks/use-dialog';

interface DestinationInfoProps {
  id: number;
  name: string;
  floor: string;
  description: string;
  maxCapacity: number;
  accessBeforeVisitInMinutes: number;
  accessAfterVisitInMinutes: number;
}

export const DestinationInfo: FC<DestinationInfoProps> = props => {
  const {
    id,
    name,
    floor,
    description,
    maxCapacity,
    accessBeforeVisitInMinutes,
    accessAfterVisitInMinutes,
    ...other
  } = props;
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const align = mdDown ? 'vertical' : 'horizontal';
  const { t } = useTranslation(['destination', 'common']);

  const [
    isOpenUpdateDestinationDialog,
    handleOpenDestinationModalOpen,
    handleCloseUpdateDestinationDialog,
  ] = useDialog();

  return (
    <>
      <Card variant="outlined" {...other}>
        <CardHeader
          title={t('destination:info.cardTitle')}
          action={
            <Button
              onClick={handleOpenDestinationModalOpen}
              color="primary"
              variant="text"
              size="large">
              {t('common:button.edit')}
            </Button>
          }
        />
        <Divider />
        <PropertyList>
          <PropertyListItem align={align} label={t('destination:info.name')} value={name} />
          <PropertyListItem align={align} label={t('destination:info.floor')} value={floor} />
          <PropertyListItem
            align={align}
            label={t('destination:info.description')}
            value={description}
          />

          <PropertyListItem
            align={align}
            label={t('destination:info.maxCap')}
            value={maxCapacity.toString()}
          />
          <PropertyListItem
            align={align}
            label={t('destination:info.accessBefore')}
            value={accessBeforeVisitInMinutes ? accessBeforeVisitInMinutes.toString() : '-'}
          />
          <PropertyListItem
            align={align}
            label={t('destination:info.accessAfter')}
            value={accessAfterVisitInMinutes ? accessAfterVisitInMinutes.toString() : '-'}
          />
        </PropertyList>
      </Card>
      <UpdateDestinationDialog
        open={isOpenUpdateDestinationDialog}
        onCancel={handleCloseUpdateDestinationDialog}
        id={id}
        name={name}
        floor={floor}
        description={description}
        maxCapacity={maxCapacity}
        accessBeforeVisitInMinutes={accessBeforeVisitInMinutes}
        accessAfterVisitInMinutes={accessAfterVisitInMinutes}
      />
    </>
  );
};
